import { Link, useNavigate } from "react-router-dom";
import { ShoppingCart, Trash2, ArrowRight } from "lucide-react";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import { QuantityStepper } from "../components/QuantityStepper";
import { useCart } from "../context/CartContext";
import { formatAUD } from "../lib/shop";

export default function CartPage() {
  const { items, updateQty, removeItem, total } = useCart();
  const navigate = useNavigate();

  if (items.length === 0) {
    return (
      <div className="mx-auto max-w-7xl px-4 py-24 text-center md:px-8" data-testid="empty-cart">
        <ShoppingCart className="mx-auto h-12 w-12 text-muted-foreground/40" />
        <h1 className="mt-4 text-2xl font-bold">Your cart is empty</h1>
        <p className="mt-2 text-sm text-muted-foreground">Browse the catalogue and add packs to get started.</p>
        <Link
          to="/"
          data-testid="empty-cart-browse-link"
          className="mt-6 inline-flex items-center gap-2 rounded-sm bg-primary px-5 py-3 text-sm font-semibold text-primary-foreground transition-colors hover:bg-secondary"
        >
          Browse products <ArrowRight className="h-4 w-4" />
        </Link>
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-7xl px-4 py-10 md:px-8" data-testid="cart-page">
      <h1 className="text-3xl font-bold tracking-tight">Cart</h1>
      <div className="mt-8 grid gap-8 lg:grid-cols-[1fr_320px]">
        <div className="overflow-x-auto rounded-sm border border-border bg-white">
          <Table>
            <TableHeader className="bg-muted">
              <TableRow>
                <TableHead>Item</TableHead>
                <TableHead>Code</TableHead>
                <TableHead className="text-right">Price / Pack</TableHead>
                <TableHead className="text-center">Qty</TableHead>
                <TableHead className="text-right">Subtotal</TableHead>
                <TableHead className="w-12"></TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {items.map((i) => (
                <TableRow key={i.variant_id} data-testid={`cart-line-${i.product_code.replace(/\s+/g, "-")}`}>
                  <TableCell className="py-3">
                    <Link to={`/products/${i.product_slug}`} className="font-medium text-foreground hover:text-secondary">
                      {i.product_name}
                    </Link>
                    <div className="text-xs text-muted-foreground">
                      {i.description}{i.insulation_colour ? ` · ${i.insulation_colour}` : ""}
                      {i.units_per_pack ? ` · ${i.units_per_pack}/pack` : ""}
                    </div>
                  </TableCell>
                  <TableCell className="font-mono text-xs">{i.product_code}</TableCell>
                  <TableCell className="text-right tabular-nums">{formatAUD(i.unit_price)}</TableCell>
                  <TableCell className="text-center">
                    <QuantityStepper
                      value={i.qty}
                      onChange={(v) => updateQty(i.variant_id, v)}
                      testId={`cart-qty-${i.product_code.replace(/\s+/g, "-")}`}
                    />
                  </TableCell>
                  <TableCell className="text-right font-semibold tabular-nums text-primary" data-testid={`cart-line-subtotal-${i.product_code.replace(/\s+/g, "-")}`}>
                    {formatAUD(i.qty * i.unit_price)}
                  </TableCell>
                  <TableCell>
                    <button
                      onClick={() => removeItem(i.variant_id)}
                      aria-label="Remove item"
                      data-testid={`cart-remove-${i.product_code.replace(/\s+/g, "-")}`}
                      className="text-muted-foreground transition-colors hover:text-destructive"
                    >
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>

        <div className="h-fit rounded-sm border border-border bg-white p-6" data-testid="cart-summary">
          <h2 className="text-base font-semibold">Order Summary</h2>
          <div className="mt-4 flex justify-between border-t border-border pt-4 text-sm">
            <span className="text-muted-foreground">Items</span>
            <span className="tabular-nums">{items.reduce((s, i) => s + i.qty, 0)}</span>
          </div>
          <div className="mt-2 flex justify-between text-base font-bold">
            <span>Total</span>
            <span className="tabular-nums text-primary" data-testid="cart-total">{formatAUD(total)}</span>
          </div>
          <p className="mt-1 text-xs text-muted-foreground">GST & shipping confirmed on invoice.</p>
          <button
            onClick={() => navigate("/checkout")}
            data-testid="proceed-to-checkout-button"
            className="mt-6 flex w-full items-center justify-center gap-2 rounded-sm bg-accent px-4 py-3 text-sm font-bold text-accent-foreground transition-colors hover:bg-[#dfa557]"
          >
            Proceed to Checkout <ArrowRight className="h-4 w-4" />
          </button>
        </div>
      </div>
    </div>
  );
}
